// Projeto: Haklai Church — Plugin WordPress Modular - Desenvolvido por: Ricardo Sarmento - https://linx.pt

import React from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useParams } from 'react-router-dom';
import api from '../services/api';
import LoadingSpinner from '../components/LoadingSpinner';
import PhotoUpload from '../components/PhotoUpload';
import AppLayout from '../components/AppLayout';
import { getRoleBadgeStyles } from '../utils/roleColors';

function MemberProfilePage() {
  const navigate = useNavigate();
  const { id } = useParams();
  const queryClient = useQueryClient();

  // Carrega dados do membro
  const { data: member, isLoading, error, refetch } = useQuery({
    queryKey: ['member', id],
    queryFn: async () => {
      const response = await api.get(`/members/${id}`);
      return response.data.data;
    },
  });

  // Carrega habilidades disponíveis
  const { data: skillsData } = useQuery({
    queryKey: ['skills'],
    queryFn: async () => {
      const response = await api.get('/skills');
      return response.data.data || [];
    },
    enabled: member?.category === 'member',
  });

  // Mutation para adicionar habilidade
  const addSkillMutation = useMutation({
    mutationFn: async (skillId) => {
      return api.post(`/members/${id}/skills`, { skill_id: skillId });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['member', id] });
      queryClient.invalidateQueries({ queryKey: ['skills'] });
    },
    onError: (error) => {
      alert('Erro ao adicionar habilidade: ' + (error.response?.data?.message || error.message));
    },
  });

  // Mutation para remover habilidade
  const removeSkillMutation = useMutation({
    mutationFn: async (skillId) => {
      return api.delete(`/members/${id}/skills/${skillId}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['member', id] });
      queryClient.invalidateQueries({ queryKey: ['skills'] });
    },
    onError: (error) => {
      alert('Erro ao remover habilidade: ' + (error.response?.data?.message || error.message));
    },
  });

  // Mutation para deletar membro
  const deleteMutation = useMutation({
    mutationFn: async () => {
      return api.delete(`/members/${id}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['members'] });
      navigate('/members');
    },
    onError: (error) => {
      alert('Erro ao deletar membro: ' + (error.response?.data?.message || error.message));
    },
  });

  // Handler de upload de foto
  const handlePhotoUploadSuccess = () => {
    refetch();
  };

  // Handler de deletar
  const handleDelete = () => {
    if (window.confirm('Tem certeza que deseja deletar este membro? Esta ação não pode ser desfeita.')) {
      deleteMutation.mutate();
    }
  };

  // Formata data
  const formatDate = (dateString) => {
    if (!dateString) return '-';
    try {
      return new Date(dateString).toLocaleDateString('pt-BR');
    } catch {
      return dateString;
    }
  };

  // Label de categoria
  const getCategoryLabel = (category) => {
    const labels = {
      visitor: 'Visitante',
      frequent: 'Frequentador Assíduo',
      frequent_encounter: 'Frequentador + Encontro',
      member: 'Membro',
    };
    return labels[category] || category;
  };

  // Badge de categoria
  const getCategoryBadge = (category) => {
    const badges = {
      visitor: 'badge-visitor',
      frequent: 'badge-frequent',
      frequent_encounter: 'badge-encounter',
      member: 'badge-member',
    };
    return badges[category] || 'badge-gray';
  };

  // Label de gênero
  const getGenderLabel = (gender) => {
    const labels = {
      male: 'Masculino',
      female: 'Feminino',
      other: 'Outro',
    };
    return labels[gender] || '-';
  };

  if (isLoading) {
    return (
      <AppLayout>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '50vh' }}>
          <LoadingSpinner size="lg" />
        </div>
      </AppLayout>
    );
  }

  if (error) {
    return (
      <AppLayout>
        <div style={{ padding: '2rem', textAlign: 'center' }}>
          <h2>Erro ao carregar membro</h2>
          <p>{error.message || 'Erro desconhecido'}</p>
          <button className="btn btn-primary" onClick={() => refetch()}>
            Tentar novamente
          </button>
          <button className="btn btn-secondary" onClick={() => navigate('/members')} style={{ marginLeft: '1rem' }}>
            Voltar para lista
          </button>
        </div>
      </AppLayout>
    );
  }

  if (!member) {
    return (
      <AppLayout>
        <div style={{ padding: '2rem', textAlign: 'center' }}>
          <h2>Membro não encontrado</h2>
          <button className="btn btn-primary" onClick={() => navigate('/members')}>
            Voltar para lista
          </button>
        </div>
      </AppLayout>
    );
  }

  return (
    <AppLayout>
      <div className="member-profile-page" style={{ padding: '2rem', maxWidth: '1200px', margin: '0 auto' }}>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '2rem' }}>
        <button
          className="btn btn-text"
          onClick={() => navigate('/members')}
        >
          <span className="material-symbols-outlined">arrow_back</span>
          Voltar
        </button>
        <div style={{ display: 'flex', gap: '1rem' }}>
          <button
            className="btn btn-secondary"
            onClick={() => navigate(`/members/${id}/edit`)}
          >
            <span className="material-symbols-outlined">edit</span>
            Editar
          </button>
          <button
            className="btn btn-danger"
            onClick={handleDelete}
            disabled={deleteMutation.isPending}
          >
            <span className="material-symbols-outlined">delete</span>
            Deletar
          </button>
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '300px 1fr', gap: '2rem' }}>
        {/* Sidebar - Foto e Info Básica */}
        <div>
          <div className="card" style={{ padding: '2rem', marginBottom: '1.5rem' }}>
            {/* Foto */}
            <div style={{ marginBottom: '1.5rem' }}>
              <PhotoUpload
                memberId={id}
                currentPhotoUrl={member.photo_url}
                currentThumbnailUrl={member.photo_thumbnail_url}
                onUploadSuccess={handlePhotoUploadSuccess}
              />
            </div>

            {/* Nome */}
            <h1 style={{ fontSize: '1.5rem', fontWeight: 'bold', marginBottom: '0.5rem', textAlign: 'center' }}>
              {member.full_name}
            </h1>

            {/* Categoria / Função */}
            <div style={{ textAlign: 'center', marginBottom: '1rem' }}>
              {member.category === 'member' && member.roles && member.roles.length > 0 ? (
                <span className="badge" style={getRoleBadgeStyles(member.roles[0])}>
                  {member.roles[0].name}
                </span>
              ) : (
                <span className={`badge ${getCategoryBadge(member.category)}`}>
                  {getCategoryLabel(member.category)}
                </span>
              )}
            </div>

            {/* Status */}
            <div style={{ textAlign: 'center', marginBottom: '1rem' }}>
              <span className={`badge ${member.is_active ? 'badge-success' : 'badge-danger'}`}>
                {member.is_active ? 'Ativo' : 'Inativo'}
              </span>
            </div>
          </div>

          {/* Informações de Contato */}
          <div className="card" style={{ padding: '1.5rem' }}>
            <h3 style={{ fontSize: '1rem', fontWeight: '600', marginBottom: '1rem' }}>Contato</h3>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
              {member.email && (
                <div>
                  <span style={{ fontSize: '0.875rem', color: 'var(--text-secondary)' }}>Email:</span>
                  <div style={{ fontWeight: '500' }}>{member.email}</div>
                </div>
              )}
              {member.phone && (
                <div>
                  <span style={{ fontSize: '0.875rem', color: 'var(--text-secondary)' }}>Telefone:</span>
                  <div style={{ fontWeight: '500' }}>{member.phone}</div>
                </div>
              )}
              {!member.email && !member.phone && (
                <div style={{ color: 'var(--text-muted)', fontSize: '0.875rem' }}>
                  Nenhum contato cadastrado
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Conteúdo Principal */}
        <div>
          {/* Informações Pessoais */}
          <div className="card" style={{ padding: '2rem', marginBottom: '1.5rem' }}>
            <h2 style={{ fontSize: '1.25rem', fontWeight: '600', marginBottom: '1.5rem' }}>
              Informações Pessoais
            </h2>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '1.5rem' }}>
              <div>
                <span style={{ fontSize: '0.875rem', color: 'var(--text-secondary)' }}>Data de Nascimento</span>
                <div style={{ fontWeight: '500', marginTop: '0.25rem' }}>
                  {formatDate(member.birth_date)}
                </div>
              </div>
              <div>
                <span style={{ fontSize: '0.875rem', color: 'var(--text-secondary)' }}>Gênero</span>
                <div style={{ fontWeight: '500', marginTop: '0.25rem' }}>
                  {getGenderLabel(member.gender)}
                </div>
              </div>
              {member.address && (
                <div style={{ gridColumn: '1 / -1' }}>
                  <span style={{ fontSize: '0.875rem', color: 'var(--text-secondary)' }}>Endereço</span>
                  <div style={{ fontWeight: '500', marginTop: '0.25rem' }}>
                    {member.address}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Células */}
          {member.cells && member.cells.length > 0 && (
            <div className="card" style={{ padding: '2rem', marginBottom: '1.5rem' }}>
              <h2 style={{ fontSize: '1.25rem', fontWeight: '600', marginBottom: '1.5rem' }}>
                Células ({member.cells.length})
              </h2>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                {member.cells.map(cell => (
                  <div
                    key={cell.id}
                    style={{
                      padding: '1rem',
                      background: 'var(--surface-hover)',
                      borderRadius: 'var(--radius-lg)',
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      cursor: 'pointer',
                    }}
                    onClick={() => navigate(`/cells/${cell.id}`)}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.background = 'var(--background-alt)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.background = 'var(--surface-hover)';
                    }}
                  >
                    <div>
                      <div style={{ fontWeight: '500' }}>{cell.name}</div>
                      {cell.is_leader && (
                        <span className="badge badge-primary" style={{ marginTop: '0.25rem', display: 'inline-block' }}>
                          Líder
                        </span>
                      )}
                    </div>
                    <span className="material-symbols-outlined" style={{ color: 'var(--text-muted)' }}>
                      chevron_right
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Encontro com Deus e Cursos */}
          <div className="card" style={{ padding: '2rem', marginBottom: '1.5rem' }}>
            <h2 style={{ fontSize: '1.25rem', fontWeight: '600', marginBottom: '1.5rem' }}>
              Encontro com Deus e Formações
            </h2>

            {/* Encontro com Deus */}
            <div style={{ marginBottom: '1.5rem' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem' }}>
                <span className="material-symbols-outlined" style={{ fontSize: '1.25rem' }}>star</span>
                <span style={{ fontWeight: '500' }}>Encontro com Deus</span>
              </div>
              {member.has_encounter ? (
                <div style={{ marginLeft: '2rem', color: 'var(--success)' }}>
                  <span className="material-symbols-outlined" style={{ fontSize: '1rem', verticalAlign: 'middle', marginRight: '0.25rem' }}>check_circle</span>
                  Participou
                  {member.encounter_date && (
                    <span style={{ marginLeft: '0.5rem', color: 'var(--text-muted)', fontSize: '0.875rem' }}>
                      em {new Date(member.encounter_date).toLocaleDateString('pt-BR')}
                    </span>
                  )}
                </div>
              ) : (
                <div style={{ marginLeft: '2rem', color: 'var(--text-muted)' }}>
                  Não participou
                </div>
              )}
            </div>

            {/* Cursos */}
            <div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.75rem' }}>
                <span className="material-symbols-outlined" style={{ fontSize: '1.25rem' }}>school</span>
                <span style={{ fontWeight: '500' }}>Formações Concluídas</span>
              </div>
              <div style={{ marginLeft: '2rem', display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  {member.completed_ctl ? (
                    <span className="material-symbols-outlined" style={{ fontSize: '1rem', color: 'var(--success)' }}>check_circle</span>
                  ) : (
                    <span className="material-symbols-outlined" style={{ fontSize: '1rem', color: 'var(--text-muted)' }}>radio_button_unchecked</span>
                  )}
                  <span style={{ color: member.completed_ctl ? 'var(--text-primary)' : 'var(--text-muted)' }}>
                    CTL - Curso de Treinamento de Líderes
                  </span>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  {member.completed_cme ? (
                    <span className="material-symbols-outlined" style={{ fontSize: '1rem', color: 'var(--success)' }}>check_circle</span>
                  ) : (
                    <span className="material-symbols-outlined" style={{ fontSize: '1rem', color: 'var(--text-muted)' }}>radio_button_unchecked</span>
                  )}
                  <span style={{ color: member.completed_cme ? 'var(--text-primary)' : 'var(--text-muted)' }}>
                    CME - Curso de Maturidade Espiritual
                  </span>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  {member.completed_stp ? (
                    <span className="material-symbols-outlined" style={{ fontSize: '1rem', color: 'var(--success)' }}>check_circle</span>
                  ) : (
                    <span className="material-symbols-outlined" style={{ fontSize: '1rem', color: 'var(--text-muted)' }}>radio_button_unchecked</span>
                  )}
                  <span style={{ color: member.completed_stp ? 'var(--text-primary)' : 'var(--text-muted)' }}>
                    STP - Seminário Teológico Pastoral
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Funções e Cargos (apenas para membros) */}
          {member.category === 'member' && member.roles && member.roles.length > 0 && (
            <div className="card" style={{ padding: '2rem', marginBottom: '1.5rem' }}>
              <h2 style={{ fontSize: '1.25rem', fontWeight: '600', marginBottom: '1.5rem' }}>
                Funções e Cargos ({member.roles.length})
              </h2>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.75rem' }}>
                {member.roles.map(role => (
                  <span
                    key={role.id}
                    className="badge"
                    style={{
                      padding: '0.5rem 1rem',
                      fontSize: '0.875rem',
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '0.5rem',
                      ...getRoleBadgeStyles(role)
                    }}
                  >
                    <span className="material-symbols-outlined" style={{ fontSize: '1.25rem', color: getRoleBadgeStyles(role).color }}>
                      badge
                    </span>
                    {role.name}
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* Habilidades (apenas para membros) */}
          {member.category === 'member' && (
            <div className="card" style={{ padding: '2rem', marginBottom: '1.5rem' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
                <h2 style={{ fontSize: '1.25rem', fontWeight: '600' }}>
                  Habilidades {member.skills && member.skills.length > 0 && `(${member.skills.length})`}
                </h2>
                <button
                  className="btn btn-primary btn-sm"
                  onClick={() => {
                    const availableSkills = skillsData?.filter(s => 
                      !member.skills?.some(ms => ms.id === s.id)
                    ) || [];
                    if (availableSkills.length === 0) {
                      alert('Todas as habilidades disponíveis já foram atribuídas a este membro.');
                      return;
                    }
                    const skillId = prompt(
                      `Escolha uma habilidade para adicionar:\n${availableSkills.map((s, i) => `${i + 1}. ${s.name}`).join('\n')}\n\nDigite o número:`
                    );
                    if (skillId) {
                      const selectedSkill = availableSkills[parseInt(skillId) - 1];
                      if (selectedSkill) {
                        addSkillMutation.mutate(selectedSkill.id);
                      }
                    }
                  }}
                  disabled={addSkillMutation.isPending}
                >
                  <span className="material-symbols-outlined" style={{ marginRight: '0.5rem' }}>add</span>
                  Adicionar Habilidade
                </button>
              </div>
              {member.skills && member.skills.length > 0 ? (
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.75rem' }}>
                  {member.skills.map(skill => (
                    <span
                      key={skill.id}
                      className="badge"
                      style={{
                        background: skill.color || 'var(--primary-light)',
                        color: skill.color ? '#fff' : 'var(--primary)',
                        padding: '0.5rem 1rem',
                        fontSize: '0.875rem',
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '0.5rem',
                        position: 'relative',
                      }}
                    >
                      {skill.icon && (
                        <span className="material-symbols-outlined" style={{ fontSize: '1rem' }}>
                          {skill.icon}
                        </span>
                      )}
                      {skill.name}
                      <button
                        onClick={() => {
                          if (window.confirm(`Remover a habilidade "${skill.name}" deste membro?`)) {
                            removeSkillMutation.mutate(skill.id);
                          }
                        }}
                        style={{
                          marginLeft: '0.5rem',
                          background: 'rgba(0, 0, 0, 0.2)',
                          border: 'none',
                          borderRadius: '50%',
                          width: '20px',
                          height: '20px',
                          display: 'inline-flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          cursor: 'pointer',
                          padding: 0,
                          color: 'inherit',
                        }}
                        disabled={removeSkillMutation.isPending}
                        title="Remover habilidade"
                      >
                        <span className="material-symbols-outlined" style={{ fontSize: '0.875rem' }}>close</span>
                      </button>
                    </span>
                  ))}
                </div>
              ) : (
                <div style={{ color: 'var(--text-muted)', fontSize: '0.875rem', marginBottom: '1rem' }}>
                  Nenhuma habilidade cadastrada
                </div>
              )}
              {skillsData && skillsData.length > 0 && member.skills && member.skills.length < skillsData.length && (
                <p style={{ fontSize: '0.875rem', color: 'var(--text-muted)', marginTop: '1rem' }}>
                  Clique em &quot;Adicionar Habilidade&quot; para atribuir novas habilidades a este membro.
                </p>
              )}
            </div>
          )}

          {/* Estatísticas */}
          <div className="card" style={{ padding: '2rem', marginBottom: '1.5rem' }}>
            <h2 style={{ fontSize: '1.25rem', fontWeight: '600', marginBottom: '1.5rem' }}>
              Estatísticas
            </h2>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '1.5rem' }}>
              <div>
                <span style={{ fontSize: '0.875rem', color: 'var(--text-secondary)' }}>Presenças Consecutivas</span>
                <div style={{ fontSize: '2rem', fontWeight: 'bold', color: 'var(--primary)', marginTop: '0.25rem' }}>
                  {member.consecutive_cell_presences || 0}
                </div>
              </div>
              <div>
                <span style={{ fontSize: '0.875rem', color: 'var(--text-secondary)' }}>Total de Presenças</span>
                <div style={{ fontSize: '2rem', fontWeight: 'bold', color: 'var(--success)', marginTop: '0.25rem' }}>
                  {member.total_presences || 0}
                </div>
              </div>
              <div>
                <span style={{ fontSize: '0.875rem', color: 'var(--text-secondary)' }}>Células</span>
                <div style={{ fontSize: '2rem', fontWeight: 'bold', color: 'var(--info)', marginTop: '0.25rem' }}>
                  {member.cells?.length || 0}
                </div>
              </div>
            </div>
          </div>

          {/* Jornada do Membro */}
          <div className="card" style={{ padding: '2rem', marginBottom: '1.5rem' }}>
            <h2 style={{ fontSize: '1.25rem', fontWeight: '600', marginBottom: '1.5rem' }}>
              Jornada do Membro
            </h2>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '1.5rem' }}>
              <div>
                <span style={{ fontSize: '0.875rem', color: 'var(--text-secondary)' }}>Primeira Visita</span>
                <div style={{ fontWeight: '500', marginTop: '0.25rem' }}>
                  {formatDate(member.first_visit_date)}
                </div>
              </div>
              {member.became_frequent_date && (
                <div>
                  <span style={{ fontSize: '0.875rem', color: 'var(--text-secondary)' }}>Tornou-se Frequentador</span>
                  <div style={{ fontWeight: '500', marginTop: '0.25rem' }}>
                    {formatDate(member.became_frequent_date)}
                  </div>
                </div>
              )}
              {member.encounter_date && (
                <div>
                  <span style={{ fontSize: '0.875rem', color: 'var(--text-secondary)' }}>Encontro com Deus</span>
                  <div style={{ fontWeight: '500', marginTop: '0.25rem' }}>
                    {formatDate(member.encounter_date)}
                  </div>
                </div>
              )}
              {member.baptism_date && (
                <div>
                  <span style={{ fontSize: '0.875rem', color: 'var(--text-secondary)' }}>Batismo nas Águas</span>
                  <div style={{ fontWeight: '500', marginTop: '0.25rem' }}>
                    {formatDate(member.baptism_date)}
                  </div>
                </div>
              )}
              {member.was_previously_baptized && (
                <div>
                  <span style={{ fontSize: '0.875rem', color: 'var(--text-secondary)' }}>Batizado Anteriormente</span>
                  <div style={{ fontWeight: '500', marginTop: '0.25rem' }}>
                    <span className="badge badge-success">Sim</span>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Informações do Sistema */}
          <div className="card" style={{ padding: '2rem' }}>
            <h2 style={{ fontSize: '1.25rem', fontWeight: '600', marginBottom: '1.5rem' }}>
              Informações do Sistema
            </h2>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '1.5rem' }}>
              <div>
                <span style={{ fontSize: '0.875rem', color: 'var(--text-secondary)' }}>Cadastrado em</span>
                <div style={{ fontWeight: '500', marginTop: '0.25rem' }}>
                  {formatDate(member.created_at)}
                </div>
              </div>
              {member.updated_at && member.updated_at !== member.created_at && (
                <div>
                  <span style={{ fontSize: '0.875rem', color: 'var(--text-secondary)' }}>Última atualização</span>
                  <div style={{ fontWeight: '500', marginTop: '0.25rem' }}>
                    {formatDate(member.updated_at)}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
      </div>
    </AppLayout>
  );
}

export default MemberProfilePage;

